import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Loader2, ChevronDown, ChevronRight, Search, Settings, ExternalLink, Plus, X, Save, RotateCcw } from 'lucide-react';

import { getConnectorFriendlyName } from '@/utils/connectorNames';

import {
  SYNC_FREQUENCY_OPTIONS,
  estToPacific,
  pacificToEst,
  SchemaEntry,
  SchemaTable,
  computeDefaultSchemaChanges,
} from '@/utils/connectionSettingsUtils';
import {
  type ConnectionDetails,
  type ConnectorDefaultSchema,
  addConnectorType,
  getConnectionDetails,
  getConnectionSchemas,
  listConnectorDefaults,
  updateConnectionSchema,
  updateConnectorDefaults,
  updateSyncSettings,
} from '@/utils/connectionSettingsApi';
import { FivetranRescheduledSyncNotice } from '@/components/settings/FivetranRescheduledSyncNotice';

interface ConnectionInfo {
  orgName: string;
  orgId: string;
  orgCreatedAt: string;
  connectorId: string;
  connectionId: string;
  service: string;
  displayName: string;
}

const RESCHEDULED_SIMULATION_CONNECTOR_ID = 'bacon_theatrics';

const ConnectionSettings: React.FC = () => {
  const { toast } = useToast();
  const [connections, setConnections] = useState<ConnectionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog state
  const [selectedConnection, setSelectedConnection] = useState<ConnectionInfo | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [details, setDetails] = useState<ConnectionDetails | null>(null);
  const [schemas, setSchemas] = useState<Record<string, SchemaEntry> | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingSchema, setLoadingSchema] = useState(false);

  // Edit state
  const [editFrequency, setEditFrequency] = useState<string>('');
  const [editDailySyncTime, setEditDailySyncTime] = useState<string>('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingSchema, setSavingSchema] = useState(false);
  const [schemaChanges, setSchemaChanges] = useState<Record<string, SchemaEntry>>({});

  // Default schema config state
  const [defaultSchemas, setDefaultSchemas] = useState<ConnectorDefaultSchema[]>([]);
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [savingDefaults, setSavingDefaults] = useState<Record<string, boolean>>({});
  const [newConnectorType, setNewConnectorType] = useState('');
  const [addingConnector, setAddingConnector] = useState(false);

  // Checkbox-based default table state
  const [defaultSchemaTableLists, setDefaultSchemaTableLists] = useState<Record<string, string[]>>({});
  const [loadingDefaultSchema, setLoadingDefaultSchema] = useState<Record<string, boolean>>({});
  const [defaultTableSearch, setDefaultTableSearch] = useState<Record<string, string>>({});
  const [defaultSyncedTables, setDefaultSyncedTables] = useState<Record<string, Set<string>>>({});
  const [defaultSchemaFallback, setDefaultSchemaFallback] = useState<Record<string, boolean>>({});

  // Fetch default schemas
  const fetchDefaults = useCallback(async () => {
    try {
      const data = await listConnectorDefaults();
      setDefaultSchemas(data);
      const synced: Record<string, Set<string>> = {};
      for (const d of data) {
        synced[d.connector_type] = new Set(Array.isArray(d.tables_to_sync) ? d.tables_to_sync : []);
      }
      setDefaultSyncedTables(synced);
    } catch (err) {
      console.error('Failed to load default schemas:', err);
    } finally {
      setLoadingDefaults(false);
    }
  }, []);

  useEffect(() => { fetchDefaults(); }, [fetchDefaults]);

  const handleSaveDefaults = async (connectorType: string) => {
    setSavingDefaults(prev => ({ ...prev, [connectorType]: true }));
    try {
      const lines = Array.from(defaultSyncedTables[connectorType] || []);
      await updateConnectorDefaults(connectorType, lines);
      toast({ title: 'Saved', description: `Default tables for ${connectorType} updated` });
      fetchDefaults();
    } catch (err) {
      console.error('Failed to save defaults:', err);
      toast({ title: 'Error', description: 'Failed to save defaults', variant: 'destructive' });
    } finally {
      setSavingDefaults(prev => ({ ...prev, [connectorType]: false }));
    }
  };

  const fetchDefaultSchemaForType = useCallback(async (connectorType: string) => {
    if (defaultSchemaTableLists[connectorType] || loadingDefaultSchema[connectorType]) return;

    // Find a connection of this type
    const sampleConn = connections.find(c => c.service === connectorType);
    if (!sampleConn) {
      setDefaultSchemaFallback(prev => ({ ...prev, [connectorType]: true }));
      return;
    }

    setLoadingDefaultSchema(prev => ({ ...prev, [connectorType]: true }));
    try {
      const schemaData = await getConnectionSchemas(sampleConn.connectorId);

      // Extract all table names from all schemas
      const allTables: string[] = [];
      for (const schema of Object.values(schemaData) as SchemaEntry[]) {
        for (const tableName of Object.keys(schema.tables || {})) {
          allTables.push(tableName);
        }
      }
      allTables.sort();
      setDefaultSchemaTableLists(prev => ({ ...prev, [connectorType]: allTables }));
    } catch (err) {
      console.error(`Failed to fetch schema for ${connectorType}:`, err);
      setDefaultSchemaFallback(prev => ({ ...prev, [connectorType]: true }));
    } finally {
      setLoadingDefaultSchema(prev => ({ ...prev, [connectorType]: false }));
    }
  }, [connections, defaultSchemaTableLists, loadingDefaultSchema]);

  const handleAddConnectorType = async () => {
    if (!newConnectorType.trim()) return;
    setAddingConnector(true);
    try {
      await addConnectorType(newConnectorType.trim());
      toast({ title: 'Added', description: `Connector type "${newConnectorType.trim()}" added` });
      setNewConnectorType('');
      fetchDefaults();
    } catch (err) {
      console.error('Failed to add connector type:', err);
      toast({ title: 'Error', description: 'Failed to add connector type', variant: 'destructive' });
    } finally {
      setAddingConnector(false);
    }
  };

  // Fetch all orgs and extract Fivetran connections
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const { data: orgs, error } = await supabase
          .from('organisations')
          .select('id, name, connections, created_at')
          .order('created_at', { ascending: false });

        if (error) throw error;

        const allConnections: ConnectionInfo[] = [];
        for (const org of orgs || []) {
          const conns = org.connections as any[];
          if (!Array.isArray(conns)) continue;

          for (const conn of conns) {
            const id = conn.id || conn.connectionId || '';
            const service = conn?.service?.toLowerCase() || conn?.type?.toLowerCase() || '';
            const isCsv = id.startsWith('csv-import-') || service === 'csv';
            const isPlaid = service === 'plaid';
            const isNonFivetran = ['file_import', 'excel'].includes(service);
            const isFileImport = isNonFivetran || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

            if (isCsv || isPlaid || isFileImport) continue;

            allConnections.push({
              orgName: org.name,
              orgId: org.id,
              orgCreatedAt: org.created_at,
              connectorId: id,
              connectionId: conn.connectionId || id,
              service: service || conn.name || 'unknown',
              displayName: getConnectorFriendlyName(service || conn.name || 'unknown'),
            });
          }
        }

        setConnections(allConnections);
      } catch (err) {
        console.error('Failed to load connections:', err);
        toast({ title: 'Error', description: 'Failed to load connections', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };

    fetchConnections();
  }, [toast]);

  const fetchDetails = useCallback(async (connectorId: string) => {
    setLoadingDetails(true);
    try {
      const data = await getConnectionDetails(connectorId);
      const isRescheduledSimulationConnector = connectorId === RESCHEDULED_SIMULATION_CONNECTOR_ID;
      const normalizedData: ConnectionDetails = isRescheduledSimulationConnector
        ? {
          ...data,
          sync_state: 'rescheduled',
          rescheduled_for: data.rescheduled_for ?? new Date(Date.now() + 60 * 60 * 1000).toISOString(),
        }
        : data;
      setDetails(normalizedData);
      setEditFrequency(String(normalizedData.sync_frequency || 360));
      setEditDailySyncTime(estToPacific(normalizedData.daily_sync_time || '03:00'));
    } catch (err) {
      console.error('Failed to fetch details:', err);
      toast({ title: 'Error', description: 'Failed to fetch connection details', variant: 'destructive' });
    } finally {
      setLoadingDetails(false);
    }
  }, [toast]);

  const fetchSchema = useCallback(async (connectorId: string) => {
    setLoadingSchema(true);
    try {
      const schemaData = await getConnectionSchemas(connectorId);
      setSchemas(schemaData);
      setSchemaChanges(JSON.parse(JSON.stringify(schemaData)));
    } catch (err) {
      console.error('Failed to fetch schema:', err);
      toast({ title: 'Error', description: 'Failed to fetch schema config', variant: 'destructive' });
    } finally {
      setLoadingSchema(false);
    }
  }, [toast]);

  const handleConnectionClick = (conn: ConnectionInfo) => {
    setSelectedConnection(conn);
    setDetails(null);
    setSchemas(null);
    setSchemaChanges({});
    setDialogOpen(true);
    fetchDetails(conn.connectorId);
    fetchSchema(conn.connectorId);
  };

  const handleSaveSettings = async () => {
    if (!selectedConnection) return;
    setSavingSettings(true);
    try {
      await updateSyncSettings(
        selectedConnection.connectorId,
        parseInt(editFrequency),
        editFrequency === '1440' ? pacificToEst(editDailySyncTime) : undefined,
      );
      toast({ title: 'Success', description: 'Sync settings updated' });
      fetchDetails(selectedConnection.connectorId);
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast({ title: 'Error', description: 'Failed to update sync settings', variant: 'destructive' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveSchema = async () => {
    if (!selectedConnection) return;
    setSavingSchema(true);
    try {
      await updateConnectionSchema(selectedConnection.connectorId, schemaChanges);
      toast({ title: 'Success', description: 'Schema config updated' });
      fetchSchema(selectedConnection.connectorId);
    } catch (err) {
      console.error('Failed to save schema:', err);
      toast({ title: 'Error', description: 'Failed to update schema config', variant: 'destructive' });
    } finally {
      setSavingSchema(false);
    }
  };

  const handleResetToDefaults = () => {
    console.log('[resetToDefaults] CLICKED - selectedConnection:', !!selectedConnection, 'schemaChanges keys:', Object.keys(schemaChanges).length);
    if (!selectedConnection || Object.keys(schemaChanges).length === 0) return;
    const entry = defaultSchemas.find(d => d.connector_type === selectedConnection.service);
    const tablesToSync = entry?.tables_to_sync ?? [];
    console.log('[resetToDefaults] service:', selectedConnection.service);
    console.log('[resetToDefaults] connector_types in defaultSchemas:', defaultSchemas.map(d => d.connector_type));
    console.log('[resetToDefaults] entry found:', entry);
    console.log('[resetToDefaults] tablesToSync:', tablesToSync);
    console.log('[resetToDefaults] schemaChanges keys:', Object.keys(schemaChanges));
    const firstSchema = Object.values(schemaChanges)[0];
    if (firstSchema) {
      console.log('[resetToDefaults] table keys in first schema:', Object.keys(firstSchema.tables || {}));
    }
    setSchemaChanges(computeDefaultSchemaChanges(schemaChanges, tablesToSync));
  };

  const toggleSchemaEnabled = (schemaName: string) => {
    setSchemaChanges((prev) => ({
      ...prev,
      [schemaName]: {
        ...prev[schemaName],
        enabled: !prev[schemaName]?.enabled,
      },
    }));
  };

  const toggleTableEnabled = (schemaName: string, tableName: string) => {
    setSchemaChanges((prev) => ({
      ...prev,
      [schemaName]: {
        ...prev[schemaName],
        tables: {
          ...prev[schemaName]?.tables,
          [tableName]: {
            ...prev[schemaName]?.tables?.[tableName],
            enabled: !prev[schemaName]?.tables?.[tableName]?.enabled,
          },
        },
      },
    }));
  };

  const filteredConnections = connections.filter((conn) => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return (
      conn.orgName.toLowerCase().includes(s) ||
      conn.displayName.toLowerCase().includes(s) ||
      conn.service.toLowerCase().includes(s) ||
      conn.connectorId.toLowerCase().includes(s)
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Default Table Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Default Table Configuration</CardTitle>
          <p className="text-sm text-muted-foreground">
            Tables checked here will be synced by default when creating new Fivetran connections of each type.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingDefaults ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading defaults...
            </div>
          ) : (
            <>
              {defaultSchemas.map((ds) => (
                <Collapsible key={ds.connector_type} onOpenChange={(isOpen) => {
                  if (isOpen) fetchDefaultSchemaForType(ds.connector_type);
                }}>
                  <div className="flex items-center gap-2 py-1">
                    <CollapsibleTrigger className="flex items-center gap-1 flex-1 text-sm font-medium">
                      <ChevronRight className="h-4 w-4 transition-transform data-[state=open]:rotate-90" />
                      {ds.connector_type}
                      <Badge variant="secondary" className="ml-2">
                        {defaultSyncedTables[ds.connector_type]?.size || 0} tables synced
                      </Badge>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="pl-6 pt-2 space-y-2">
                    {loadingDefaultSchema[ds.connector_type] ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Fetching tables from Fivetran...
                      </div>
                    ) : defaultSchemaTableLists[ds.connector_type] ? (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1 max-w-xs">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <Input
                              placeholder="Search tables..."
                              className="pl-8 h-8 text-xs"
                              value={defaultTableSearch[ds.connector_type] || ''}
                              onChange={(e) => setDefaultTableSearch(prev => ({ ...prev, [ds.connector_type]: e.target.value }))}
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-8"
                            onClick={() => {
                              const allSet = new Set(defaultSchemaTableLists[ds.connector_type]);
                              setDefaultSyncedTables(prev => ({ ...prev, [ds.connector_type]: allSet }));
                            }}
                          >
                            Select All
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs h-8"
                            onClick={() => {
                              setDefaultSyncedTables(prev => ({ ...prev, [ds.connector_type]: new Set() }));
                            }}
                          >
                            Deselect All
                          </Button>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto border rounded-md p-2 space-y-0.5">
                          {defaultSchemaTableLists[ds.connector_type]
                            .filter(t => {
                              const search = (defaultTableSearch[ds.connector_type] || '').toLowerCase();
                              return !search || t.toLowerCase().includes(search);
                            })
                            .map(tableName => {
                              const cleanName = tableName
                                .replace(/^[a-z0-9_]+\.\s*/i, '')
                                .replace(/^figabl_[a-z0-9]+_/i, '');
                              return (
                                <div key={tableName} className="flex items-center gap-2 py-0.5 px-2">
                                  <Checkbox
                                    checked={defaultSyncedTables[ds.connector_type]?.has(tableName) || false}
                                    onCheckedChange={(checked) => {
                                      setDefaultSyncedTables(prev => {
                                        const next = new Set(prev[ds.connector_type] || []);
                                        if (checked) next.add(tableName);
                                        else next.delete(tableName);
                                        return { ...prev, [ds.connector_type]: next };
                                      });
                                    }}
                                  />
                                  <span className="text-sm">{cleanName}</span>
                                </div>
                              );
                            })}
                        </div>
                      </>
                    ) : defaultSchemaFallback[ds.connector_type] ? (
                      <p className="text-xs text-muted-foreground">
                        No active connection found for "{ds.connector_type}" — cannot fetch table list.
                        Manage tables manually in the disabled list above.
                      </p>
                    ) : null}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSaveDefaults(ds.connector_type)}
                        disabled={savingDefaults[ds.connector_type]}
                      >
                        {savingDefaults[ds.connector_type] ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Save className="mr-2 h-3 w-3" />}
                        Save
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        Last updated: {ds.updated_at ? new Date(ds.updated_at).toLocaleDateString() : 'never'}
                      </span>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}

              {/* Add new connector type */}
              <div className="flex items-center gap-2 pt-2 border-t">
                <Input
                  placeholder="New connector type (e.g. google_sheets)"
                  value={newConnectorType}
                  onChange={(e) => setNewConnectorType(e.target.value)}
                  className="max-w-xs text-sm"
                />
                <Button size="sm" variant="outline" onClick={handleAddConnectorType} disabled={addingConnector || !newConnectorType.trim()}>
                  {addingConnector ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Plus className="mr-2 h-3 w-3" />}
                  Add Type
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="bg-white rounded-md shadow">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">Fivetran Connections</h2>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
            <Input
              placeholder="Search connections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Organisation</TableHead>
              <TableHead>Service</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredConnections.length > 0 ? (
              filteredConnections.map((conn) => (
                <TableRow
                  key={`${conn.orgId}-${conn.connectorId}`}
                  className="cursor-pointer"
                  onClick={() => handleConnectionClick(conn)}
                >
                  <TableCell className="font-medium">{conn.orgName}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{getConnectorFriendlyName(conn.service)}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleConnectionClick(conn); }}>
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {searchTerm ? 'No connections match your search' : 'No Fivetran connections found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Connection Settings Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedConnection?.displayName} — {selectedConnection?.orgName}
            </DialogTitle>
            <DialogDescription asChild>
              <a
                href={`https://fivetran.com/dashboard/connections/${selectedConnection?.connectorId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                Open in Fivetran <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </DialogDescription>
          </DialogHeader>

          {/* Sync Settings Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Sync Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingDetails ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                </div>
              ) : details ? (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Historical sync:</span>
                    <Badge variant={details.is_historical_sync ? 'warning' : 'default'}>
                      {details.is_historical_sync ? 'In Progress' : 'Complete'}
                    </Badge>
                  </div>

                  <FivetranRescheduledSyncNotice
                    sync_state={details.sync_state}
                    rescheduled_for={details.rescheduled_for}
                    sync_tasks={details.sync_tasks}
                    sync_warnings={details.sync_warnings}
                    status_reason={details.status_reason}
                  />

                  <div className="flex items-center gap-4">
                    <div className="space-y-1 flex-1">
                      <label className="text-sm font-medium">Sync Frequency</label>
                      <Select value={editFrequency} onValueChange={setEditFrequency}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SYNC_FREQUENCY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {editFrequency === '1440' && (
                      <div className="space-y-1">
                        <label className="text-sm font-medium">Daily Sync Time (Pacific)</label>
                        <div className="flex items-center gap-1">
                          <Select
                            value={editDailySyncTime.split(':')[0] || '00'}
                            onValueChange={(h) => {
                              const mins = editDailySyncTime.split(':')[1] || '00';
                              setEditDailySyncTime(`${h}:${mins}`);
                            }}
                          >
                            <SelectTrigger className="w-20">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-background z-50">
                              {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map((h) => (
                                <SelectItem key={h} value={h}>{h}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <span className="text-sm font-medium">:</span>
                          <Select
                            value={editDailySyncTime.split(':')[1] || '00'}
                            onValueChange={(m) => {
                              const hrs = editDailySyncTime.split(':')[0] || '00';
                              setEditDailySyncTime(`${hrs}:${m}`);
                            }}
                          >
                            <SelectTrigger className="w-20">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-background z-50">
                              {['00', '30'].map((m) => (
                                <SelectItem key={m} value={m}>{m}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button onClick={handleSaveSettings} disabled={savingSettings} size="sm">
                    {savingSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Sync Settings
                  </Button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Failed to load details</p>
              )}
            </CardContent>
          </Card>

          {/* Schema / Tables Card — hidden for Google Sheets */}
          {selectedConnection?.service !== 'google_sheets' && <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Tables</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {loadingSchema ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading schema...
                </div>
              ) : schemaChanges && Object.keys(schemaChanges).length > 0 ? (
                <>
                  {Object.entries(schemaChanges).flatMap(([schemaName, schema]) =>
                    Object.entries(schema.tables || {}).map(([tableName, table]) => {
                      // Strip schema prefix from table name for display
                      const displayName = tableName.replace(/^[a-z0-9_]+\.\s*/i, '');
                      // Also strip Fivetran-generated prefixes like "figabl_xxxxx_"
                      const cleanName = displayName.replace(/^figabl_[a-z0-9]+_/i, '');
                      return (
                        <div key={`${schemaName}-${tableName}`} className="flex items-center gap-2 py-0.5 px-2">
                          <Checkbox
                            checked={table.enabled}
                            onCheckedChange={() => toggleTableEnabled(schemaName, tableName)}
                          />
                          <span className="text-sm">{cleanName}</span>
                        </div>
                      );
                    })
                  )}
                  {Object.values(schemaChanges).every(s => Object.keys(s.tables || {}).length === 0) && (
                    <span className="text-xs text-muted-foreground">No tables available</span>
                  )}
                  <div className="flex items-center gap-2 mt-3">
                    <Button onClick={handleSaveSchema} disabled={savingSchema} size="sm">
                      {savingSchema && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Table Config
                    </Button>
                    {/* <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetToDefaults}
                      disabled={loadingSchema || Object.keys(schemaChanges).length === 0}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset to defaults
                    </Button> */}
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No schema data available</p>
              )}
            </CardContent>
          </Card>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Collapsible schema section
const SchemaCollapsible: React.FC<{
  schemaName: string;
  schema: SchemaEntry;
  onToggleSchema: () => void;
  onToggleTable: (tableName: string) => void;
}> = ({ schemaName, schema, onToggleSchema, onToggleTable }) => {
  const [open, setOpen] = useState(false);
  const tables = schema.tables || {};
  const tableCount = Object.keys(tables).length;
  const enabledCount = Object.values(tables).filter((t) => t.enabled).length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-2 py-1 px-2 rounded hover:bg-muted/50">
        <Checkbox
          checked={schema.enabled}
          onCheckedChange={onToggleSchema}
          onClick={(e) => e.stopPropagation()}
        />
        <CollapsibleTrigger className="flex items-center gap-1 flex-1 text-sm font-medium text-left">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {schemaName}
          <span className="text-xs text-muted-foreground ml-2">
            ({enabledCount}/{tableCount} tables)
          </span>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="pl-10 space-y-1 pb-2">
        {Object.entries(tables).map(([tableName, table]) => (
          <div key={tableName} className="flex items-center gap-2 py-0.5">
            <Checkbox
              checked={table.enabled}
              onCheckedChange={() => onToggleTable(tableName)}
            />
            <span className="text-sm">{tableName}</span>
          </div>
        ))}
        {tableCount === 0 && (
          <span className="text-xs text-muted-foreground">No tables in this schema</span>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ConnectionSettings;
